import { jsx as _jsx } from "react/jsx-runtime";
import classnames from 'classnames';
import { forwardRef, useId } from 'react';
// The tooltip element
let tip;
if (typeof document !== 'undefined') {
    tip = document.createElement('div');
    tip.className = 'pds-tooltip';
}
// A timer that will show the tooltip.
let showTimer;
// A timer that will hide the tooltip.
let hideTimer;
// The current element with a tooltip, if there is one.
let current;
// The next element with a tooltip, after a delay.
let next;
// Get the offset of an element relative to the document.
function getOffset(element) {
    let top = element.offsetTop;
    let left = element.offsetLeft;
    while (element.offsetParent) {
        element = element.offsetParent;
        top += element.offsetTop;
        left += element.offsetLeft;
    }
    return { top, left };
}
// Hide the tooltip.
export function hide() {
    if (typeof document === 'undefined')
        return;
    if (!current)
        return;
    current = null;
    tip.classList.remove('pds-tooltip-visible');
    tip.dispatchEvent(new CustomEvent('tooltip-hide', { bubbles: true }));
}
// Show the tooltip.
function show(target) {
    if (typeof document === 'undefined')
        return;
    hide();
    current = target;
    clearTimeout(hideTimer);
    const fixed = target.hasAttribute('data-tooltip-fixed');
    if (!tip.parentElement)
        document.body.appendChild(tip);
    const id = target.getAttribute('aria-describedby');
    if (id) {
        const content = document.getElementById(id)?.cloneNode(true);
        if (content) {
            content.hidden = false;
            content.removeAttribute('id');
            tip.innerHTML = content.outerHTML;
        }
    }
    else {
        // Clear the title so it doesn't show in addition to the tooltip.
        if (!target.getAttribute('data-tooltip-html')) {
            target.setAttribute('data-tooltip-html', target.title);
            target.title = '';
        }
        const html = target.getAttribute('data-tooltip-html');
        tip.innerHTML = `<div class='pds-tooltip-sm'>${html}</div>`;
    }
    const { top, left } = getOffset(target);
    tip.style.position = fixed ? 'fixed' : '';
    const placement = target.dataset.tooltipPlacement || 'top';
    tip.classList.toggle('pds-tooltip-top', placement === 'top');
    tip.classList.toggle('pds-tooltip-bottom', placement === 'bottom');
    tip.classList.toggle('pds-tooltip-left', placement === 'left');
    tip.classList.toggle('pds-tooltip-right', placement === 'right');
    switch (placement) {
        case 'top':
            tip.style.top = `${top - tip.offsetHeight}px`;
            break;
        case 'bottom':
            tip.style.top = `${top + target.offsetHeight}px`;
            break;
        case 'left':
        case 'right':
            tip.style.top = `${Math.ceil(top + target.offsetHeight / 2 - tip.offsetHeight / 2)}px`;
            break;
    }
    switch (placement) {
        case 'top':
        case 'bottom':
            tip.style.left = `${Math.ceil(left + target.offsetWidth / 2 - tip.offsetWidth / 2)}px`;
            break;
        case 'left':
            tip.style.left = `${left - tip.offsetWidth - 4}px`;
            break;
        case 'right':
            tip.style.left = `${left + target.offsetWidth + 4}px`;
            break;
    }
    tip.classList.add('pds-tooltip-visible');
    tip.dispatchEvent(new CustomEvent('tooltip-show', { bubbles: true }));
}
// Show the next tooltip, after a delay.
function showNext() {
    if (!next)
        return;
    show(next);
    next = null;
}
// Show tooltips on click, if appropriate.
function click(event) {
    let target = event.target;
    target = target.closest('[data-click-tooltip]');
    if (target == null || current === target) {
        hide();
    }
    else {
        show(target);
    }
}
// Show the tooltip when the mouse enters a trigger or the tooltip iself.
function mouseenter(event) {
    const target = event.target;
    if (!target)
        return;
    if (target === tip || target === current) {
        if (!current?.hasAttribute?.('data-tooltip-hide-on-hover'))
            clearTimeout(hideTimer);
    }
    else if (target.hasAttribute?.('data-tooltip')) {
        if (target.hasAttribute?.('data-tooltip-delay')) {
            const delay = parseInt(target.dataset.tooltipDelay || '0');
            next = target;
            clearTimeout(showTimer);
            showTimer = window.setTimeout(showNext, delay);
        }
        else {
            show(target);
        }
    }
}
// Hide the tooltip when the mouse leaves the trigger or the tooltip itself.
function mouseleave(event) {
    const target = event.target;
    if (target === next) {
        next = null;
        clearTimeout(showTimer);
    }
    if (target === tip || target === current) {
        hideTimer = window.setTimeout(hide, 200);
    }
}
// Hide the tooltip when pressing ESC.
function keydown({ key }) {
    if (key === 'Escape')
        hide();
}
if (typeof window !== 'undefined' && !window.PDS_TOOLTIPS) {
    // Prevent adding multiple handlers on the same page.
    window.PDS_TOOLTIPS = true;
    document.addEventListener('click', click);
    document.addEventListener('keydown', keydown);
    document.addEventListener('mouseenter', mouseenter, true);
    document.addEventListener('mouseleave', mouseleave, true);
}
export const tooltipTargetProps = (id, options) => {
    const tooltipParentProps = {};
    tooltipParentProps['data-tooltip'] = true;
    tooltipParentProps['data-tooltip-placement'] = options.position;
    tooltipParentProps['aria-describedby'] = id;
    return tooltipParentProps;
};
export const renderTooltip = (id, options) => {
    const tooltipClasses = classnames({
        'pds-tooltip-sm': options.size && options.size == 'sm',
        'pds-tooltip-md': options.size && options.size == 'md',
    });
    return (_jsx("div", { id: id, className: tooltipClasses, hidden: true, children: typeof options.content === 'function'
            ? options.content()
            : options.content }));
};
export const useTooltip = (options) => {
    const id = useId();
    if (!options) {
        return null;
    }
    const element = renderTooltip(id, options);
    const targetProps = tooltipTargetProps(id, options);
    return { element, targetProps };
};
/**
 * Primary UI component for user interaction
 */
export const Tooltip = forwardRef(function Tooltip(props, ref) {
    const { className: classNameProp, id, size, content, visible = false, } = props;
    const className = classnames(classNameProp, {
        'pds-tooltip-sm': size && size == 'sm',
        'pds-tooltip-md': size && size == 'md',
        'pds-tooltip-visible': visible,
    });
    return (_jsx("div", { id: id, ref: ref, className: className, hidden: !visible, children: typeof content === 'function' ? content() : content }));
});
